/* eslint-disable jsx-a11y/html-has-lang */
import React from 'react';
import { Helmet } from 'react-helmet';
import PropTypes from 'prop-types';
import { getHost } from '../../../../lib/host';
import {
  parseStringifiedData,
  getSchemaOrgJSONLD,
  getPageSeoData
} from '../lib/SEOHelper';

const computeFullPath = pathname => `${getHost()}/merchantapps${pathname}`;

export default function SEO({
  app: {
    context: {
      locale: { country = '', locale = '', language = '' }
    }
  },
  mainImageUrl = '',
  location: { pathname = '' },
  categorySlug = '',
  detailsSlug = '',
  seoData = {}
}) {
  if (
    typeof seoData !== 'object' ||
    seoData === null ||
    !Object.keys(seoData).length
  )
    return null; // don't update head for countries/locales without any SEO meta content
  // return SEO meta content for each country by enabling in App Center Main SEO interaction in PS and adding the country/locale
  const contextCountry = country.toLowerCase();
  const fullPath = computeFullPath(pathname);
  const {
    title = '',
    description = '',
    imgUrl = '',
    structuredData = '',
    parent = {},
    mainTitle
  } = getPageSeoData(seoData, categorySlug, detailsSlug);
  const {
    openGraph,
    canonical,
    imgUrlParsed,
    mainImageUrlParsed
  } = parseStringifiedData({
    structuredData,
    parent,
    title,
    imgUrl,
    mainImageUrl
  });
  const schemaOrgJSONLD = getSchemaOrgJSONLD({
    mainImageUrlParsed,
    imgUrlParsed,
    title,
    language,
    country: contextCountry,
    pathname,
    fullPath,
    categorySlug,
    detailsSlug,
    mainTitle,
    openGraph
  });

  function getCanonical() {
    if (categorySlug && !detailsSlug && !canonical) {
      return null;
    }
    return <link rel="canonical" href={fullPath} />;
  }

  /**
   * rosettaHreflangs()
   * TODO: We will either innersource with marketeting team or build our own solution when it comes to scaling this out globally,
   * right now only US rosetta languages send back SEO data. Ideally, we would designate all locales and countries in our  sitemap.
   * @returns {JSX} Returns an Array of hreflang alternate links to rosetta languages at current full path.
   */
  function rosettaHreflangs() {
    if (contextCountry !== 'us') return null;
    return ['en', 'fr', 'zh', 'es'].map(lang => {
      return (
        <link
          rel="alternate"
          hrefLang={`${lang}-${contextCountry}`}
          href={`${fullPath}?locale.x=${lang}_${country}`}
        />
      );
    });
  }

  return (
    <Helmet>
      <html prefix="og: http://ogp.me/ns#" />
      <title>{title}</title>
      <meta name="description" content={description} />
      <script type="application/ld+json">
        {JSON.stringify(schemaOrgJSONLD)}
      </script>
      <meta name="image" content={imgUrlParsed} />
      <meta
        property="og:title"
        content={openGraph?.title ? openGraph.title : title}
      />
      {rosettaHreflangs()}
      {getCanonical()}
      <meta
        property="og:description"
        content={openGraph?.description ? openGraph.description : description}
      />
      <meta property="og:url" content={fullPath} />
      <meta property="og:locale" content={locale} />
      <meta property="og:image" content={imgUrlParsed} />
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content={mainTitle} />
    </Helmet>
  );
}

SEO.propTypes = {
  seoData: PropTypes.objectOf(PropTypes.object).isRequired,
  mainImageUrl: PropTypes.string,
  path: PropTypes.string,
  locale: PropTypes.string,
  categorySlug: PropTypes.string,
  detailsSlug: PropTypes.string,
  dir: PropTypes.string,
  app: PropTypes.object.isRequired,
  location: PropTypes.object.isRequired
};
